import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SupplierCreditNoteDetailSchema, SupplierCreditNoteSchema } from '@taqon/contracts';
import { Link as RouterLink, useParams } from 'react-router';
import { getData, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { refreshAfterPosting } from './cache';
import { DocumentLines, Facts, JournalRef, RecordFailure, RecordLink } from './detail';
import { RefusalState } from './refusal';

/**
 * One supplier credit note (`/supplier-credit-notes/:id`): the supplier, the invoice it credits
 * (linked), its reference, dates and reason, the lines with their accounts, dimensions and the VAT
 * retained at capture, the totals, the status and, once posted, its journal (FRD-FIN-075,
 * FRD-VAT-037; SDD §8.9). A direct link or a refresh opens it; posting carries `If-Match`.
 */
export function CreditNoteDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const canPost = hasCapability(session.data, 'finance.supplier_credit_note:post');
  const detail = useQuery({
    queryKey: ['supplier-credit-note', id],
    queryFn: () =>
      getData(`/api/v1/finance/supplier-credit-notes/${id}`, SupplierCreditNoteDetailSchema),
  });
  const post = useMutation({
    mutationFn: (note: { id: string; version: number }) =>
      postJson(
        `/api/v1/finance/supplier-credit-notes/${note.id}/post`,
        {},
        SupplierCreditNoteSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: note.version },
      ),
    onSuccess: () => refreshAfterPosting(queryClient),
  });

  const back = (
    <Button component={RouterLink} to="/supplier-credit-notes" startIcon={<ArrowBackIcon />}>
      All credit notes
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the credit note" />;
  if (detail.isError) {
    return (
      <RecordFailure
        title="Supplier credit note"
        noun="credit note"
        error={detail.error}
        back={back}
      />
    );
  }

  const { creditNote, invoice } = detail.data;
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`Supplier credit note ${creditNote.documentNo}`}
        {...(detail.data.supplier.summary === null
          ? {}
          : { description: detail.data.supplier.summary })}
        actions={
          <>
            {back}
            {canPost && creditNote.status === 'draft' && (
              <Button
                variant="contained"
                disabled={post.isPending}
                onClick={() => {
                  post.mutate({ id: creditNote.id, version: creditNote.version });
                }}
              >
                {post.isPending ? 'Posting…' : 'Post'}
              </Button>
            )}
          </>
        }
      />
      {post.isError && <RefusalState error={post.error} />}
      <Section title="Credit note">
        <Facts
          label="Credit note details"
          facts={[
            ['Status', <StatusChip key="s" status={creditNote.status} />],
            ['Supplier', detail.data.supplier.summary ?? 'Not visible'],
            [
              'Invoice credited',
              <RecordLink key="i" to={`/supplier-invoices/${invoice.id}`}>
                {invoice.documentNo} ({invoice.supplierInvoiceNumber})
              </RecordLink>,
            ],
            ['Invoice outstanding', invoice.outstandingAmount],
            ["Supplier's credit note reference", creditNote.supplierCreditNoteReference],
            ['Credit note date', creditNote.creditNoteDate],
            ['Tax date', creditNote.taxDate],
            ['Reason', creditNote.reason],
            ['Net', creditNote.netTotal],
            ['VAT', creditNote.vatTotal],
            ['Gross', creditNote.grossTotal],
            ['Journal', <JournalRef key="journal" journal={detail.data.journal} />],
          ]}
        />
      </Section>
      <Section title="Lines">
        <DocumentLines label="Credit note lines" lines={detail.data.lines} creditNote />
      </Section>
      <DocumentsPanel
        recordType="finance.supplier_credit_note"
        recordId={creditNote.id}
        canAttach={
          hasCapability(session.data, 'finance.supplier_credit_note:create') ||
          hasCapability(session.data, 'finance.supplier_credit_note:change')
        }
        title="Supporting documents"
      />
      <RecordHistory recordType="finance.supplier_credit_note" recordId={creditNote.id} />
    </Stack>
  );
}
